/**
 * PantryLocationsManager — CRUD modal for user-defined pantry storage
 * locations. Supports:
 *   • Custom display name (Kitchen / Fridge / Garage / etc.)
 *   • Private alias ("Blue Box", "Cabinet X") to obscure sensitive
 *     locations. When is_private=true, the alias is what shows in
 *     shared views and events.
 *   • Icon + optional photo of the location itself
 *
 * Reads /api/pantry/locations, POST/PATCH/DELETE same. Auto-seeds
 * five common defaults on first-run (backend handles that).
 */
import { useEffect, useRef, useState } from "react";
import {
  X, Plus, Edit3, Trash2, Save, Loader2, Lock, Unlock, Camera, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const RED   = "#7B2A2E";

const ICONS = ["🥫","🧊","❄️","🍽️","🚗","🪟","🛒","🏠","📦","🏺","🔒","🍷"];

export default function PantryLocationsManager({ open, onClose, onChange }) {
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // location being edited or null
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/pantry/locations");
      setLocs(r.data?.items || []);
    } catch { setLocs([]); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const saveLocation = async (loc) => {
    try {
      if (loc.id) {
        const { id, ...body } = loc;
        await http.patch(`/pantry/locations/${id}`, body);
      } else {
        await http.post("/pantry/locations", loc);
      }
      await load();
      setEditing(null); setCreating(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Delete this location? Items stored here will keep their entry but lose the location link.")) return;
    try {
      await http.delete(`/pantry/locations/${id}`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
           style={{ color: GREEN }}
           data-testid="pantry-locations-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EED9AC" }}>
          <div className="font-semibold">Manage storage locations</div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-black/5"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline opacity-50" /></div>
          ) : (
            <>
              {(editing || creating) ? (
                <LocationForm
                  initial={editing || {}}
                  onSave={saveLocation}
                  onCancel={() => { setEditing(null); setCreating(false); }}
                />
              ) : (
                <>
                  <ul className="space-y-2 mb-4" data-testid="pantry-locations-list">
                    {locs.map((l) => (
                      <li key={l.id}
                          className="rounded-2xl border p-3 flex items-center gap-3"
                          style={{ borderColor: "#E9D8B4" }}
                          data-testid={`pantry-loc-row-${l.id}`}>
                        <span className="text-xl flex-shrink-0">{l.icon || "📦"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold flex items-center gap-1 truncate">
                            {l.is_private ? <Lock className="w-3 h-3 opacity-70" /> : null}
                            {l.is_private ? (l.alias || l.name) : l.name}
                          </div>
                          <div className="text-[11px] opacity-60 truncate">
                            {l.location_type ? `${l.location_type} · ` : ""}
                            {l.item_count} item{l.item_count === 1 ? "" : "s"}
                            {l.is_private && l.alias && l.name && (
                              <span className="ml-1 opacity-50">· real: {l.name}</span>
                            )}
                          </div>
                        </div>
                        <button onClick={() => setEditing(l)} className="p-1.5 hover:bg-black/5 rounded-full"
                                data-testid={`pantry-loc-edit-${l.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteLocation(l.id)} className="p-1.5 hover:bg-red-50 rounded-full text-red-600"
                                data-testid={`pantry-loc-delete-${l.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => setCreating(true)}
                          className="w-full rounded-full py-3 font-semibold text-white flex items-center justify-center gap-2"
                          style={{ background: GREEN }}
                          data-testid="pantry-loc-add">
                    <Plus className="w-4 h-4" /> Add a new location
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LocationForm({ initial, onSave, onCancel }) {
  const [name, setName]       = useState(initial.name || "");
  const [alias, setAlias]     = useState(initial.alias || "");
  const [icon, setIcon]       = useState(initial.icon || "📦");
  const [type, setType]       = useState(initial.location_type || "");
  const [isPrivate, setIsPriv]= useState(!!initial.is_private);
  const [photoUrl, setPhotoUrl] = useState(initial.photo_url || null);
  const [notes, setNotes]     = useState(initial.notes || "");
  const [busy, setBusy]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadPhoto = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const up = await http.post("/lists/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(up.data?.url || null);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give the location a name.");
    if (isPrivate && !alias.trim()) return toast.error("Add an alias so the private label has something to show.");
    setBusy(true);
    await onSave({
      id: initial.id, name: name.trim(),
      alias: alias.trim() || null,
      icon, location_type: type || null,
      is_private: isPrivate,
      photo_url: photoUrl,
      notes: notes.trim() || null,
    });
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="pantry-loc-form">
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Icon</label>
        <div className="flex flex-wrap gap-1.5">
          {ICONS.map((i) => (
            <button key={i} onClick={() => setIcon(i)}
                    className={`w-10 h-10 rounded-xl border-2 text-xl transition ${icon === i ? "bg-amber-100" : ""}`}
                    style={{ borderColor: icon === i ? AMBER : "#E9D8B4" }}>
              {i}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Name *</label>
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Kitchen Pantry" className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
               style={{ borderColor: "#E9D8B4" }} data-testid="pantry-loc-name" />
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm outline-none bg-white"
                style={{ borderColor: "#E9D8B4" }}>
          <option value="">— choose —</option>
          <option value="pantry">Pantry</option>
          <option value="fridge">Refrigerator</option>
          <option value="freezer">Freezer</option>
          <option value="kitchen">Kitchen counter/cupboard</option>
          <option value="cellar">Cellar / basement</option>
          <option value="garage">Garage</option>
          <option value="vehicle">Vehicle</option>
          <option value="office">Office</option>
          <option value="emergency-kit">Emergency kit</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Privacy toggle ---------------------------------- */}
      <div className="rounded-2xl p-3 border" style={{ borderColor: "#E9D8B4", background: "#FFF7E4" }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPriv(e.target.checked)}
                 className="w-4 h-4" data-testid="pantry-loc-private" />
          <span className="text-sm font-semibold flex items-center gap-1">
            {isPrivate ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            Private location · use an alias in shared views
          </span>
        </label>
        {isPrivate && (
          <div className="mt-2">
            <input value={alias} onChange={(e) => setAlias(e.target.value)}
                   placeholder="e.g. Blue Box · Cabinet X · Shelf Alpha"
                   className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                   style={{ borderColor: "#E9D8B4", background: "white" }}
                   data-testid="pantry-loc-alias" />
            <p className="text-[10px] opacity-70 mt-1">
              Only you and invited household members see the real name. Alias is what shows on shared timelines.
            </p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Photo of the location (optional)</label>
        {photoUrl ? (
          <div className="flex items-center gap-2">
            <img src={photoUrl.startsWith("http") ? photoUrl : `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                 alt="" className="w-20 h-20 object-cover rounded-lg" />
            <button onClick={() => setPhotoUrl(null)} className="text-xs underline opacity-70">remove</button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
                  className="rounded-full border-2 border-dashed px-3 py-2 text-xs font-semibold flex items-center gap-1"
                  style={{ borderColor: AMBER, color: GREEN }}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            Add photo
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
               onChange={(e) => uploadPhoto(e.target.files?.[0])} />
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} className="rounded-full px-4 py-2 text-sm font-semibold border"
                style={{ borderColor: GREEN, color: GREEN }} data-testid="pantry-loc-cancel">
          Cancel
        </button>
        <button onClick={save} disabled={busy}
                className="flex-1 rounded-full px-4 py-2 text-sm font-semibold text-white flex items-center justify-center gap-2"
                style={{ background: GREEN }} data-testid="pantry-loc-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
        </button>
      </div>
    </div>
  );
}
